import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Search } from 'lucide-react';
import api, { SupportCategoryResponse } from '@/lib/api';
import { useFaq } from '../hooks';
import { FaqAccordion } from '../components';
import { ARCHE_PATH } from '../constants';

export function FaqPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<SupportCategoryResponse[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const { faqs, loading } = useFaq();

  const fetchCategories = useCallback(async () => {
    try {
      const result = await api.listSupportCategoriesUser();
      setCategories(result.categories);
    } catch {
      // Categories are non-critical — FAQs still render without them
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedCategories(new Set());
    setSearchQuery('');
  };

  // Build category name lookup
  const categoryNameMap = new Map(categories.map((c) => [c.categoryId, c.name]));

  // Apply filters
  let filteredFaqs = faqs;

  if (selectedCategories.size > 0) {
    filteredFaqs = filteredFaqs.filter(
      (faq) => faq.categoryId && selectedCategories.has(faq.categoryId),
    );
  }

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filteredFaqs = filteredFaqs.filter(
      (faq) => faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q),
    );
  }

  const hasActiveFilters = selectedCategories.size > 0 || searchQuery.trim().length > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Back navigation */}
      <Link to={ARCHE_PATH}>
        <Button variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Support
        </Button>
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">Frequently Asked Questions</h1>
        <p className="text-muted-foreground mt-1">
          Find answers to common questions about the platform.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          aria-label="Search FAQs"
        />
      </div>

      {/* Category Filters */}
      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Filter by:</span>
          {categories.map((cat) => (
            <Button
              key={cat.categoryId}
              variant={selectedCategories.has(cat.categoryId) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleCategory(cat.categoryId)}
              className="text-xs"
            >
              {cat.name}
            </Button>
          ))}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
              Clear all
            </Button>
          )}
        </div>
      )}

      {/* FAQ List */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : filteredFaqs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {hasActiveFilters
              ? 'No FAQs match your filters. Try different keywords or categories.'
              : 'No FAQs available yet.'}
          </p>
          {hasActiveFilters && (
            <Button variant="link" onClick={clearFilters} className="mt-2">
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <FaqAccordion faqs={filteredFaqs} categoryNameMap={categoryNameMap} />
      )}
    </div>
  );
}
